import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { TestErrorBoundary } from './UserTypeErrorBoundaries';
import { TestLoading, LoadingOverlay } from './LoadingStates';
import { NetworkAwareRetry } from './RetryMechanism';
import ErrorDisplay from './ErrorDisplay';
import { useErrorHandling, useNetworkAwareOperation } from '../hooks/useErrorHandling';
import type { PVTTestState, StimulusConfig, TestType } from '../types';
import { generateTargetLetters, generateStimulusLetters } from '../utils/letterStimulus';
import { LAPSE_THRESHOLD_MS, MAJOR_LAPSE_THRESHOLD_MS } from '../config/testMetrics';

interface TestInterfaceProps {
  testId?: string;
  userType: 'admin' | 'guest' | 'kiosk';
  testConfig?: {
    testDuration: number;
    userId?: string;
    sessionId?: string;
    testType?: TestType;
    isMockup?: boolean;
  };
  onTestComplete?: (results: {
    reactionTimes: number[];
    lapses: number;
    falseStarts: number;
    totalResponses: number;
    averageReactionTime: number;
    medianReactionTime: number;
    majorLapses: number;
    minorLapses: number;
    missedTargets: number;
    fastestTenMean: number;
    slowestTenMean: number;
    reactionTimeStdDev: number;
    alertnessLevel: string;
    testDuration: number;
  }) => void;
}

// How long the 5-letter stimulus stays on screen in LETTER mode before it
// auto-advances (a target letter left unanswered counts as a lapse).
const LETTER_STIMULUS_WINDOW_MS = 2000;
const MOCKUP_DURATION_MS = 30000;

interface MockupResults {
  averageReactionTime: number;
  totalResponses: number;
  falseStarts: number;
  lapses: number;
}

const formatDuration = (durationMs: number) => {
  if (durationMs < 60000) return `${durationMs / 1000} seconds`;
  const minutes = Math.floor(durationMs / 60000);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

const DEFAULT_CONFIG: StimulusConfig = {
  minInterval: 1000, // 1 second
  maxInterval: 5000, // 5 seconds
  testDuration: 600000, // 10 minutes
};

const TestInterface: React.FC<TestInterfaceProps> = ({
  testId,
  userType,
  testConfig,
  onTestComplete
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [testState, setTestState] = useState<PVTTestState>({
    isActive: false,
    currentStimulus: false,
    stimulusStartTime: 0,
    reactionTimes: [],
    lapses: 0,
    falseStarts: 0,
    testStartTime: 0,
    nextStimulusTimeout: 0,
  });

  const [counter, setCounter] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showPreTest, setShowPreTest] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSavingResults, setIsSavingResults] = useState(false);
  // LETTER mode state
  const [targetLetters, setTargetLetters] = useState<string[]>([]);
  const [stimulusLetters, setStimulusLetters] = useState<string[] | null>(null);
  
  const {
    error: testError,
    handleError,
    clearError,
    executeWithErrorHandling,
  } = useErrorHandling({
    showUserFeedback: true,
    logErrors: true,
    maxRetries: 3,
    onError: (error) => {
      console.error('Test interface error:', error);
      if (error.type === 'timing' && testState.isActive) {
        setTestState(prev => ({ ...prev, isActive: false }));
      }
    },
  });

  const { isOnline, executeOrQueue } = useNetworkAwareOperation();
  
  // Refs for timing and cleanup
  const testAreaRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const testDurationRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stimulusStartTimeRef = useRef<number>(0);
  const stopTestRef = useRef<(() => void) | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  // LETTER mode refs (timeouts read these to avoid stale closures)
  const stimulusWindowRef = useRef<NodeJS.Timeout | null>(null);
  const targetLettersRef = useRef<string[]>([]);
  const stimulusHasTargetRef = useRef<boolean>(false);
  // Mirrors of testState.isActive / testState.currentStimulus so response
  // handling can stay out of setState updater side effects (StrictMode safe)
  const isActiveRef = useRef<boolean>(false);
  const stimulusActiveRef = useRef<boolean>(false);

  const playFeedbackTone = useCallback((type: 'good' | 'lapse') => {
    try {
      const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const context = audioContextRef.current;
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = type === 'good' ? 880 : 220;
      gainNode.gain.value = 0.0001;

      oscillator.connect(gainNode);
      gainNode.connect(context.destination);

      const now = context.currentTime;
      gainNode.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);

      oscillator.start(now);
      oscillator.stop(now + 0.22);
    } catch (error) {
      console.warn('Audio feedback unavailable:', error);
    }
  }, []);

  const getEffectiveTestConfig = () => {
    if (testConfig) {
      return testConfig;
    }
    
    if (userType === 'guest') {
      const storedConfig = sessionStorage.getItem('guestTestConfig');
      if (storedConfig) {
        try {
          return JSON.parse(storedConfig);
        } catch (error) {
          console.warn('Failed to parse stored guest test config:', error);
        }
      }
    }
    
    return null;
  };

  const effectiveTestConfig = getEffectiveTestConfig();
  const isMockup = Boolean(effectiveTestConfig?.isMockup);
  const actualTestDuration = effectiveTestConfig?.testDuration || DEFAULT_CONFIG.testDuration;
  
  const finalTestConfig = {
    ...DEFAULT_CONFIG,
    testDuration: actualTestDuration,
    userId: effectiveTestConfig?.userId,
    sessionId: effectiveTestConfig?.sessionId,
    testType: effectiveTestConfig?.testType || 'CHECKERBOARD' as TestType
  };

  const [mockupPhase, setMockupPhase] = useState(isMockup);
  const [runDuration, setRunDuration] = useState(isMockup ? MOCKUP_DURATION_MS : actualTestDuration);
  const [mockupResults, setMockupResults] = useState<MockupResults | null>(null);
  const mockupRunRef = useRef(isMockup);
  const runDurationRef = useRef(runDuration);

  const isLetterMode = finalTestConfig.testType === 'LETTER';
  const liveResponseLapses = testState.reactionTimes.filter((rt) => rt >= LAPSE_THRESHOLD_MS).length;
  const liveMissedTargets = Math.max(0, testState.lapses - liveResponseLapses);
  const durationLabel = formatDuration(runDuration);
  const liveAverageReactionTime = testState.reactionTimes.length > 0
    ? Math.round(testState.reactionTimes.reduce((a, b) => a + b, 0) / testState.reactionTimes.length)
    : null;

  // Initialize component
  useEffect(() => {
    const initializeTest = () => {
      try {
        setIsInitializing(true);
        
        if (!finalTestConfig.testDuration || finalTestConfig.testDuration < 1000) {
          console.warn('Invalid test duration, using default');
        }
        
        if (finalTestConfig.minInterval >= finalTestConfig.maxInterval) {
          console.warn('Invalid stimulus interval, using defaults');
        }
        
        if (isLetterMode && targetLettersRef.current.length === 0) {
          // Generate the 3 letters to memorize before the pre-test screen
          const targets = generateTargetLetters();
          targetLettersRef.current = targets;
          setTargetLetters(targets);
        }

        setTimeRemaining(runDuration);
        setIsInitializing(false);
      } catch (error) {
        console.error('Test initialization error:', error);
        setIsInitializing(false);
      }
    };

    initializeTest();
  }, [runDuration]);

  // Generate random interval between min and max
  const getRandomInterval = useCallback(() => {
    return Math.random() * (finalTestConfig.maxInterval - finalTestConfig.minInterval) + finalTestConfig.minInterval;
  }, [finalTestConfig.minInterval, finalTestConfig.maxInterval]);

  // Schedule next stimulus with proper ISI
  const scheduleNextStimulus = useCallback(() => {
    const interval = getRandomInterval();

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (!isActiveRef.current) return;

      setTestState(prev => {
        if (!prev.isActive) return prev;

        const startTime = performance.now();
        stimulusStartTimeRef.current = startTime;
        isProcessingRef.current = false; // Reset processing flag when stimulus appears

        return {
          ...prev,
          currentStimulus: true,
          stimulusStartTime: startTime,
        };
      });

      stimulusActiveRef.current = true;
      setCounter(prev => prev + 1);

      if (isLetterMode) {
        const { letters, hasTarget } = generateStimulusLetters(targetLettersRef.current);
        stimulusHasTargetRef.current = hasTarget;
        setStimulusLetters(letters);

        // LETTER mode: the stimulus only stays up for a fixed window. If it
        // expires unanswered, a target letter counts as a lapse (miss);
        // a non-target stimulus is a correct rejection.
        if (stimulusWindowRef.current) clearTimeout(stimulusWindowRef.current);
        stimulusWindowRef.current = setTimeout(() => {
          if (!isActiveRef.current) return;

          const missed = stimulusHasTargetRef.current;
          stimulusHasTargetRef.current = false;
          stimulusActiveRef.current = false;
          setStimulusLetters(null);
          isProcessingRef.current = true;

          if (missed) {
            playFeedbackTone('lapse');
          }

          setTestState(prev => {
            if (!prev.isActive || !prev.currentStimulus) return prev;
            return {
              ...prev,
              currentStimulus: false,
              lapses: prev.lapses + (missed ? 1 : 0),
              stimulusStartTime: 0,
            };
          });

          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            isProcessingRef.current = false;
            scheduleNextStimulus();
          }, 1000);
        }, LETTER_STIMULUS_WINDOW_MS);
      }
    }, interval);
  }, [getRandomInterval, isLetterMode, playFeedbackTone]);

  // Handle user response with proper validation
  const handleResponse = useCallback(() => {
    try {
      // Prevent multiple rapid responses
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      const responseTime = performance.now();

      if (!isActiveRef.current) {
        isProcessingRef.current = false;
        return;
      }

      // Note: all setTestState updaters below are pure (no side effects) —
      // React StrictMode double-invokes updaters in dev.

      // False start - response before stimulus appears
      if (!stimulusActiveRef.current) {
        setTestState(prev => ({
          ...prev,
          falseStarts: prev.falseStarts + 1,
        }));

        // ISI after false start
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          isProcessingRef.current = false;
          scheduleNextStimulus();
        }, 1000);
        return;
      }

      const reactionTime = responseTime - stimulusStartTimeRef.current;

      // LETTER mode: responding when none of the target letters is shown
      // is a commission error (false start, no RT recorded).
      if (isLetterMode && !stimulusHasTargetRef.current) {
        if (stimulusWindowRef.current) clearTimeout(stimulusWindowRef.current);
        stimulusHasTargetRef.current = false;
        stimulusActiveRef.current = false;
        setStimulusLetters(null);

        setTestState(prev => ({
          ...prev,
          currentStimulus: false,
          falseStarts: prev.falseStarts + 1,
          stimulusStartTime: 0,
        }));

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          isProcessingRef.current = false;
          scheduleNextStimulus();
        }, 1000);
        return;
      }

      // Validate reaction time - must be at least 10ms
      if (reactionTime < 10) {
        console.warn('Invalid reaction time detected:', reactionTime);
        isProcessingRef.current = false;
        return;
      }

      const isLapse = reactionTime >= LAPSE_THRESHOLD_MS;

      // Valid response
      if (stimulusWindowRef.current) clearTimeout(stimulusWindowRef.current);
      stimulusHasTargetRef.current = false;
      stimulusActiveRef.current = false;
      setStimulusLetters(null);

      playFeedbackTone(isLapse ? 'lapse' : 'good');

      setTestState(prev => ({
        ...prev,
        currentStimulus: false,
        reactionTimes: [...prev.reactionTimes, reactionTime],
        lapses: prev.lapses + (isLapse ? 1 : 0),
        stimulusStartTime: 0,
      }));

      // ISI - Mandatory 1 second delay between stimuli
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        isProcessingRef.current = false;
        scheduleNextStimulus();
      }, 1000);
    } catch (error) {
      isProcessingRef.current = false;
      handleError(error, 'Response handling error');
    }
  }, [isLetterMode, scheduleNextStimulus, handleError, playFeedbackTone]);

  // Start a run. Mockup runs first (30s), then the actual test uses the selected duration.
  const startTest = useCallback((duration = runDuration, mockupRun = mockupPhase) => {
    const startTime = performance.now();
    mockupRunRef.current = mockupRun;
    runDurationRef.current = duration;

    setMockupPhase(mockupRun);
    setRunDuration(duration);
    setTestState({
      isActive: true,
      currentStimulus: false,
      stimulusStartTime: 0,
      reactionTimes: [],
      lapses: 0,
      falseStarts: 0,
      testStartTime: startTime,
      nextStimulusTimeout: 0,
    });
    
    setCounter(0);
    setTimeRemaining(duration);
    setShowPreTest(false);
    setStimulusLetters(null);
    stimulusHasTargetRef.current = false;
    stimulusActiveRef.current = false;
    isActiveRef.current = true;
    isProcessingRef.current = false;
    
    // Auto-focus test area for immediate keyboard response
    setTimeout(() => {
      testAreaRef.current?.focus();
    }, 100);
    
    // Schedule first stimulus
    scheduleNextStimulus();
    
    // Set test duration timeout
    if (testDurationRef.current) clearTimeout(testDurationRef.current);
    testDurationRef.current = setTimeout(() => {
      if (stopTestRef.current) {
        stopTestRef.current();
      }
    }, duration);
    
    // Start progress tracking
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    progressIntervalRef.current = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        if (stopTestRef.current) {
          stopTestRef.current();
        }
      }
    }, 100);
  }, [scheduleNextStimulus, runDuration, mockupPhase]);

  const startActualTest = () => {
    setMockupResults(null);
    startTest(finalTestConfig.testDuration, false);
  };

  const handleEndTestEarly = async () => {
    await stopTest(true);
  };

  // Stop the test
  const stopTest = useCallback(async (completedEarly = false) => {
    console.log('Stopping test...');
    try {
      setIsSavingResults(!mockupRunRef.current);
      isProcessingRef.current = true; // Prevent any responses after test stops
      isActiveRef.current = false;
      stimulusActiveRef.current = false;
      
      setTestState(prev => {
        const finalState = { ...prev, isActive: false, currentStimulus: false };
        return finalState;
      });
      
      // Clear all timeouts and intervals
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (testDurationRef.current) {
        clearTimeout(testDurationRef.current);
        testDurationRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (stimulusWindowRef.current) {
        clearTimeout(stimulusWindowRef.current);
        stimulusWindowRef.current = null;
      }
      stimulusHasTargetRef.current = false;
      setStimulusLetters(null);
      
      // Calculate final results with proper rounding
      const averageReactionTime = testState.reactionTimes.length > 0 
        ? Math.ceil(testState.reactionTimes.reduce((a, b) => a + b, 0) / testState.reactionTimes.length)
        : 0;
      
      const lowestReactionTime = testState.reactionTimes.length > 0
        ? Math.ceil(Math.min(...testState.reactionTimes))
        : 0;
      
      const highestReactionTime = testState.reactionTimes.length > 0
        ? Math.ceil(Math.max(...testState.reactionTimes))
        : 0;
      
      const actualTestDuration = (runDurationRef.current - timeRemaining) / 1000;
      
      const sortedReactionTimes = [...testState.reactionTimes].sort((a, b) => a - b);
      const reactionCount = testState.reactionTimes.length;
      const medianReactionTime = reactionCount === 0
        ? 0
        : reactionCount % 2 === 1
          ? sortedReactionTimes[Math.floor(reactionCount / 2)]
          : (sortedReactionTimes[reactionCount / 2 - 1] + sortedReactionTimes[reactionCount / 2]) / 2;
      const missedTargets = isLetterMode ? liveMissedTargets : 0;
      const majorLapses = testState.reactionTimes.filter((rt) => rt > MAJOR_LAPSE_THRESHOLD_MS).length + missedTargets;
      const minorLapses = testState.reactionTimes.filter((rt) => rt >= LAPSE_THRESHOLD_MS && rt <= MAJOR_LAPSE_THRESHOLD_MS).length;
      const sliceCount = Math.max(1, Math.floor(reactionCount * 0.1));
      const fastestSlice = sortedReactionTimes.slice(0, sliceCount);
      const slowestSlice = sortedReactionTimes.slice(-sliceCount);
      const fastestTenMean = fastestSlice.length > 0
        ? fastestSlice.reduce((acc, value) => acc + value, 0) / fastestSlice.length
        : 0;
      const slowestTenMean = slowestSlice.length > 0
        ? slowestSlice.reduce((acc, value) => acc + value, 0) / slowestSlice.length
        : 0;
      const meanReactionTime = averageReactionTime;
      const variance = reactionCount > 0
        ? testState.reactionTimes.reduce((acc, value) => acc + Math.pow(value - meanReactionTime, 2), 0) / reactionCount
        : 0;
      const reactionTimeStdDev = Math.sqrt(variance);
      const alertnessLevel = meanReactionTime > 290 && medianReactionTime > 295 ? 'reduced' : 'normal';

      const results = {
        reactionTimes: testState.reactionTimes,
        lapses: testState.lapses,
        falseStarts: testState.falseStarts,
        totalResponses: testState.reactionTimes.length,
        averageReactionTime,
        lowestReactionTime,
        highestReactionTime,
        medianReactionTime,
        majorLapses,
        minorLapses,
        missedTargets,
        fastestTenMean,
        slowestTenMean,
        reactionTimeStdDev,
        alertnessLevel,
        testDuration: actualTestDuration,
        completedEarly,
      };
      console.log('Test results:', results);

      // Mockup results are shown locally and never persisted.
      if (mockupRunRef.current) {
        setMockupResults({
          averageReactionTime,
          totalResponses: results.totalResponses,
          falseStarts: results.falseStarts,
          lapses: results.lapses,
        });
        setTimeRemaining(0);
        setIsSavingResults(false);
        return;
      }
      
      // Validate results before saving
      if (results.reactionTimes.length === 0) {
        throw new Error('No valid responses recorded. Test results cannot be saved.');
      }
      
      // Call completion callback if provided
      if (onTestComplete) {
        onTestComplete(results);
        setIsSavingResults(false);
        return;
      }

      // Default navigation behavior based on user type
      const saveResults = async () => {
        const { apiService } = await import('../services/apiService');
        
        if (userType === 'guest') {
          const storedUserInfo = sessionStorage.getItem('userInfo');
          const guestInfo = storedUserInfo ? JSON.parse(storedUserInfo) : {};
          
          if (testId) {
            await executeOrQueue(
              () => apiService.submitGuestTestResults(
                testId,
                {
                  name: guestInfo.name,
                  age: guestInfo.age,
                  company: guestInfo.company,
                },
                results
              ),
              { executeOffline: false, queueWhenOffline: true }
            );
          }
          
          sessionStorage.setItem('testResults', JSON.stringify({
            ...results,
            testId,
            completedAt: new Date().toISOString(),
            userType: 'guest'
          }));
          
          navigate(`/guest/results/${testId || 'session'}`);
        } else if (userType === 'kiosk') {
          const storedSession = sessionStorage.getItem('kioskSession');
          const kioskSession = storedSession ? JSON.parse(storedSession) : null;
          if ((!kioskSession?.companyCode && !kioskSession?.companyId) || !kioskSession?.employeeId) {
            throw new Error('Kiosk session not found. Please restart the test.');
          }

          if (testId) {
            await executeOrQueue(
              () => apiService.submitKioskTestResults(
                testId,
                {
                  companyCode: kioskSession.companyCode,
                  companyId: kioskSession.companyId,
                  employeeId: kioskSession.employeeId,
                  ...results
                }
              ),
              { executeOffline: false, queueWhenOffline: true }
            );
          }

          navigate(`/kiosk/results/${testId}`);
        } else {
          if (testId && user) {
            await executeOrQueue(
              () => apiService.submitTestResults(
                testId,
                results
              ),
              { executeOffline: false, queueWhenOffline: true }
            );
          }
          
          navigate(`/admin/results/${testId}`);
        }
      };

      await executeWithErrorHandling(saveResults, 'Save test results');
      
      setTimeRemaining(0);
      setIsSavingResults(false);
      
    } catch (error) {
      setIsSavingResults(false);
      handleError(error, 'Failed to save test results');

      if (mockupRunRef.current) {
        setShowPreTest(true);
        setTimeRemaining(MOCKUP_DURATION_MS);
        return;
      }
      
      if (userType === 'guest') {
        navigate(`/guest/results/${testId || 'session'}`);
      } else if (userType === 'kiosk') {
        navigate(`/kiosk/results/${testId}`);
      } else if (userType === 'admin') {
        navigate(`/admin/results/${testId}`);
      } else {
        navigate(`/admin/results/${testId}`);
      }
    }
  }, [testState, timeRemaining, onTestComplete, userType, testId, user, navigate, executeWithErrorHandling, executeOrQueue, handleError, isLetterMode, liveMissedTargets]);

  // Assign stopTest to ref for use in startTest
  stopTestRef.current = stopTest;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (testDurationRef.current) clearTimeout(testDurationRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (stimulusWindowRef.current) clearTimeout(stimulusWindowRef.current);
    };
  }, []);

  // Keyboard events during test - properly handles false starts
  useEffect(() => {
    if (!testState.isActive) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        handleResponse();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [testState.isActive, handleResponse]);

  // Pre-test keyboard shortcut
  useEffect(() => {
    if (!showPreTest) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        startTest();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [showPreTest, startTest]);

  const getUserDisplayName = () => {
    if (userType === 'guest') {
      const storedUserInfo = sessionStorage.getItem('userInfo');
      return storedUserInfo ? JSON.parse(storedUserInfo).name : 'Guest User';
    }
    if (userType === 'kiosk') {
      const storedSession = sessionStorage.getItem('kioskSession');
      const kioskSession = storedSession ? JSON.parse(storedSession) : null;
      return kioskSession?.employeeName || kioskSession?.employeeId || 'Employee';
    }
    return user?.name || 'User';
  };

  const handleTestRestart = () => {
    clearError();
    setMockupResults(null);
    setShowPreTest(true);
    setTestState({
      isActive: false,
      currentStimulus: false,
      stimulusStartTime: 0,
      reactionTimes: [],
      lapses: 0,
      falseStarts: 0,
      testStartTime: 0,
      nextStimulusTimeout: 0,
    });
    setCounter(0);
    setTimeRemaining(runDurationRef.current);
    setStimulusLetters(null);
    stimulusHasTargetRef.current = false;
    stimulusActiveRef.current = false;
    isActiveRef.current = false;
    if (isLetterMode) {
      // Fresh set of letters to memorize on restart
      const targets = generateTargetLetters();
      targetLettersRef.current = targets;
      setTargetLetters(targets);
    }
    isProcessingRef.current = false;
  };

  if (isInitializing) {
    return (
      <TestLoading
        stage="initializing"
        userType={userType}
        progress={50}
      />
    );
  }

  if (testError && testError.type === 'timing') {
    return (
      <TestErrorBoundary
        testId={testId}
        userType={userType}
        onTestRestart={handleTestRestart}
      >
        <div />
      </TestErrorBoundary>
    );
  }

  // Pre-test screen
  if (showPreTest) {
    return (
      <TestErrorBoundary
        testId={testId}
        userType={userType}
        onTestRestart={handleTestRestart}
      >
        <NetworkAwareRetry
          fallbackMessage="Test requires an internet connection for result saving."
          showQueueStatus={true}
        >
          <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
            <div className="bg-navy-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
              {testError && (
                <div className="mb-6">
                  <ErrorDisplay
                    error={testError}
                    onRetry={testError.retryable ? () => clearError() : undefined}
                    onDismiss={clearError}
                    variant="inline"
                  />
                </div>
              )}

              <div className="mb-6">
                <div className="bg-navy-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {userType === 'guest'
                    ? 'Fatigue Check'
                    : userType === 'kiosk'
                    ? 'Kiosk Test'
                    : 'Fatigue Check'}
                </h1>
                <div className="text-navy-200 mb-2">
                  <p>Welcome, {getUserDisplayName()}</p>
                </div>
              </div>

              {isLetterMode && (
                <div className="bg-navy-900/60 border border-gold-500/40 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-gold-400 mb-3">Memorize these 3 letters:</h3>
                  <div className="flex justify-center gap-3" role="group" aria-label={`Letters to memorize: ${targetLetters.join(', ')}`}>
                    {targetLetters.map((letter) => (
                      <span
                        key={letter}
                        className="bg-navy-700 text-white font-mono font-bold text-3xl w-12 h-12 flex items-center justify-center rounded-lg border border-navy-600"
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-navy-900/60 border border-navy-700 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gold-400 mb-2">Instructions:</h3>
                <ul className="text-sm text-navy-100 space-y-1 text-left">
                  {isLetterMode ? (
                    <>
                      <li>• Remember the 3 letters shown above</li>
                      <li>• 5 random letters will appear at 1-5 second intervals</li>
                      <li>• Respond only if one of YOUR 3 letters is among the 5 shown</li>
                      <li>• Don't respond when none of your letters appear (false start)</li>
                      <li>• Stay focused for the entire {durationLabel}</li>
                      <li>• Use mouse click, touch, or spacebar/enter</li>
                    </>
                  ) : (
                    <>
                      <li>• Click the checkerboard pattern as quickly as possible when it appears</li>
                      <li>• Checkerboard pattern appears at 1-5 second intervals</li>
                      <li>• Don't click before the checkerboard pattern appears (false start)</li>
                      <li>• Stay focused for the entire {durationLabel}</li>
                      <li>• Use mouse click, touch, or spacebar/enter</li>
                    </>
                  )}
                </ul>
              </div>

              {mockupPhase && (
                <p className="mb-4 text-sm text-navy-200">
                  Mockup first: 30 seconds. These results are for practice only and will not be saved.
                </p>
              )}

              <Button
                onClick={() => startTest(runDuration, mockupPhase)}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-4 px-6 text-lg font-semibold"
                size="lg"
                disabled={!!testError && !testError.retryable}
              >
                {mockupPhase ? 'Start Mockup Test' : 'Start Test'}
              </Button>
              
              <p className="text-xs text-navy-300 mt-3">
                Press spacebar or enter to start
              </p>
            </div>
          </div>
        </NetworkAwareRetry>
      </TestErrorBoundary>
    );
  }

  // Mockup results (shown locally, never saved)
  if (mockupResults) {
    return (
      <TestErrorBoundary
        testId={testId}
        userType={userType}
        onTestRestart={handleTestRestart}
      >
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
          <div className="bg-navy-800 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Mockup Complete</h1>
            <p className="text-sm text-navy-200 mb-6">
              These results are for practice only and were not saved.
            </p>

            <div className="grid grid-cols-2 gap-4 text-left mb-6">
              <div className="rounded-lg bg-navy-900/60 border border-navy-700 p-4">
                <div className="text-xs text-navy-300">Average Reaction Time</div>
                <div className="text-xl font-semibold text-gold-400">
                  {mockupResults.totalResponses > 0 ? `${mockupResults.averageReactionTime}ms` : '—'}
                </div>
              </div>
              <div className="rounded-lg bg-navy-900/60 border border-navy-700 p-4">
                <div className="text-xs text-navy-300">Responses</div>
                <div className="text-xl font-semibold text-white">{mockupResults.totalResponses}</div>
              </div>
              <div className="rounded-lg bg-navy-900/60 border border-navy-700 p-4">
                <div className="text-xs text-navy-300">False Starts</div>
                <div className="text-xl font-semibold text-red-400">{mockupResults.falseStarts}</div>
              </div>
              <div className="rounded-lg bg-navy-900/60 border border-navy-700 p-4">
                <div className="text-xs text-navy-300">Total Lapses</div>
                <div className="text-xl font-semibold text-yellow-400">{mockupResults.lapses}</div>
              </div>
            </div>

            <p className="text-xs text-navy-300 mb-4">
              Actual test: {formatDuration(finalTestConfig.testDuration)}
            </p>

            <Button
              onClick={startActualTest}
              className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-4 px-6 text-lg font-semibold"
              size="lg"
            >
              Start Actual Test
            </Button>
          </div>
        </div>
      </TestErrorBoundary>
    );
  }

  // Main test interface
  return (
    <TestErrorBoundary
      testId={testId}
      userType={userType}
      onTestRestart={handleTestRestart}
    >
      <NetworkAwareRetry
        fallbackMessage="Test is running offline. Results will be saved when connection is restored."
        showQueueStatus={true}
      >
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
          
          <LoadingOverlay
            isVisible={isSavingResults}
            message="Saving test results..."
            showProgress={false}
          />
          
          {testError && testError.type !== 'timing' && (
            <div className="fixed top-4 left-4 right-4 z-20">
              <ErrorDisplay
                error={testError}
                onRetry={testError.retryable ? () => clearError() : undefined}
                onDismiss={clearError}
                variant="banner"
              />
            </div>
          )}
      
          {/* Header with mockup live stats on the left and end-test control on the right */}
          <div className="p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {mockupPhase && testState.isActive && (
                <div
                  className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-navy-100"
                  role="group"
                  aria-label="Live test statistics"
                >
                  <div>
                    <span className="text-navy-300">Average Reaction Time:</span>
                    <span className="ml-2 font-medium text-gold-400">
                      {liveAverageReactionTime === null ? '—' : `${liveAverageReactionTime}ms`}
                    </span>
                  </div>
                  <div>
                    <span className="text-navy-300">Responses:</span>
                    <span className="ml-2 font-medium">{testState.reactionTimes.length}</span>
                  </div>
                  <div>
                    <span className="text-navy-300">False Starts:</span>
                    <span className="ml-2 font-medium text-red-400">{testState.falseStarts}</span>
                  </div>
                  <div>
                    <span className="text-navy-300">Total Lapses:</span>
                    <span className="ml-2 font-medium text-yellow-400">{testState.lapses}</span>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              onClick={handleEndTestEarly}
              className="border-navy-600 text-gold-400 hover:bg-navy-800 shrink-0"
            >
              End Test Early
            </Button>
          </div>

          {/* Main test area - fills the whole space between header and footer */}
          <div
            ref={testAreaRef}
            className="flex-1 w-full bg-navy-800 cursor-pointer relative focus:outline-none focus:ring-4 focus:ring-gold-500/60"
            style={!isLetterMode && testState.currentStimulus ? {
              backgroundImage: 'linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff), linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #ffffff 75%, #ffffff)',
              backgroundSize: '320px 320px',
              backgroundPosition: '0 0, 160px 160px',
              backgroundColor: '#000000'
            } : undefined}
            onClick={handleResponse}
            onTouchStart={(e) => {
              e.preventDefault();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleResponse();
            }}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleResponse();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={
              isLetterMode
                ? testState.currentStimulus && stimulusLetters
                  ? `Test area - Letters ${stimulusLetters.join(' ')} are showing (trial ${counter}). Respond only if one of your memorized letters appears.`
                  : 'Test area - Waiting for letters to appear. Stay ready.'
                : testState.currentStimulus
                ? `Test area - Checkerboard pattern is showing (trial ${counter}). Click to respond.`
                : 'Test area - Waiting for checkerboard pattern to appear. Stay ready.'
            }
            aria-live="polite"
            aria-atomic="true"
          >
            {isLetterMode && testState.currentStimulus && stimulusLetters && (
              <div className="absolute inset-0 flex items-center justify-center gap-3 sm:gap-6 px-4">
                {stimulusLetters.map((letter, index) => (
                  <span
                    key={`${letter}-${index}`}
                    className="text-white font-mono font-bold"
                    style={{ fontSize: 'clamp(2.5rem, 10vw, 6rem)' }}
                  >
                    {letter}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Instructions footer */}
          <div className="bg-navy-800/90 backdrop-blur-sm text-navy-200 p-4 text-center text-sm border-t border-navy-600">
            {isLetterMode
              ? 'Respond only when one of your 3 memorized letters appears among the 5 shown. Use mouse, touch, or spacebar/enter.'
              : 'Click the checkerboard pattern as quickly as possible when it appears. Use mouse, touch, or spacebar/enter.'}
            {!isOnline && (
              <span className="ml-4 text-amber-500">
                • Running offline - results will be saved when connection is restored
              </span>
            )}
          </div>
        </div>
      </NetworkAwareRetry>
    </TestErrorBoundary>
  );
};

export default TestInterface;
